/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
// TODO(mmalerba): See if we can clean this up at some point.
import moment, { Moment, MomentFormatSpecification, MomentInput } from 'moment';

/**
 * Configurable options for {@see MomentDateAdapter}.
 * @deprecated use IGenericDateAdapterOptions, will be removed in future versions
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface MatErpDateAdapterOptions {

    /**
     * When enabled, the dates have to match the format exactly.
     * See https://momentjs.com/guides/#/parsing/strict-mode/.
     */
    strict?: boolean;

    /**
     * Turns the use of utc dates on or off.
     * Changing this will change how Angular Material components like DatePicker output dates.
     * {@default false}
     */
    useUtc?: boolean;
}

/** Configurable options for {@see MomentDateAdapter}. */
export interface IMatErpDateAdapterOptions {

    /**
     * When enabled, the dates have to match the format exactly.
     * See https://momentjs.com/guides/#/parsing/strict-mode/.
     */
    strict?: boolean;

    /**
     * Turns the use of utc dates on or off.
     * Changing this will change how Angular Material components like DatePicker output dates.
     * {@default false}
     */
    useUtc?: boolean;
}

/** InjectionToken for moment date adapter to configure options. */
export const MAT_ERP_DATE_ADAPTER_OPTIONS: InjectionToken<IMatErpDateAdapterOptions> = new InjectionToken(
    'MAT_ERP_DATE_ADAPTER_OPTIONS', {
        providedIn: 'root',
        factory: MAT_ERP_DATE_ADAPTER_OPTIONS_FACTORY,
    });

/** @docs-private */
export function MAT_ERP_DATE_ADAPTER_OPTIONS_FACTORY(): IMatErpDateAdapterOptions {
    return {
        useUtc: false,
    };
}

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
    const valuesArray = Array(length);
    for (let i = 0; i < length; i++) {
        valuesArray[ i ] = valueFunction(i);
    }
    return valuesArray;
}

/**
 * Adapts Erp Dates for use with Angular Material
 * Usage:
 * { provide: DateAdapter, useClass: ErpDateAdapter, deps: [MAT_DATE_LOCALE, MAT_ERP_DATE_ADAPTER_OPTIONS] },
 */
@Injectable()
export class ErpDateAdapter extends DateAdapter<string> {
    // Note: all of the methods that accept a `Moment` input parameter immediately call `this.toMoment`
    // on it. This is to ensure that we're working with a `Moment` that has the correct locale setting
    // while avoiding mutating the original object passed to us. Just calling `.locale(...)` on the
    // input would mutate the object.

    private _localeData: {
        firstDayOfWeek: number;
        longMonths: string[];
        shortMonths: string[];
        dates: string[];
        longDaysOfWeek: string[];
        shortDaysOfWeek: string[];
        narrowDaysOfWeek: string[];
    };

    constructor(
        @Optional()
        @Inject(MAT_DATE_LOCALE)
        public dateLocale: string,
        @Optional()
        @Inject(MAT_ERP_DATE_ADAPTER_OPTIONS)
        private _options?: IMatErpDateAdapterOptions,
    ) {
        super();
        this.setLocale(dateLocale || moment.locale());
    }

    public setLocale(locale: string): void {
        super.setLocale(locale);

        const momentLocaleData: moment.Locale = moment.localeData(locale);
        this._localeData = {
            firstDayOfWeek: momentLocaleData.firstDayOfWeek(),
            longMonths: momentLocaleData.months(),
            shortMonths: momentLocaleData.monthsShort(),
            dates: range(31, (i) => this.toMoment(this.createDate(2017, 0, i + 1)).format('D')),
            longDaysOfWeek: momentLocaleData.weekdays(),
            shortDaysOfWeek: momentLocaleData.weekdaysShort(),
            narrowDaysOfWeek: momentLocaleData.weekdaysMin(),
        };
    }

    public getYear(date: string): number {
        return this.toMoment(date).year();
    }

    public getMonth(date: string): number {
        return this.toMoment(date).month();
    }

    public getDate(date: string): number {
        return this.toMoment(date).date();
    }

    public getDayOfWeek(date: string): number {
        return this.toMoment(date).day();
    }

    public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
        // Moment.js doesn't support narrow month names, so we just use short if narrow is requested.
        return style === 'long' ? this._localeData.longMonths : this._localeData.shortMonths;
    }

    public getDateNames(): string[] {
        return this._localeData.dates;
    }

    public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
        switch (style) {
            case 'long':
                return this._localeData.longDaysOfWeek;
            case 'short':
                return this._localeData.shortDaysOfWeek;
            default:
                return this._localeData.narrowDaysOfWeek;
        }
    }

    public getYearName(date: string): string {
        return this.toMoment(date).format('YYYY');
    }

    public getFirstDayOfWeek(): number {
        return this._localeData.firstDayOfWeek;
    }

    public getNumDaysInMonth(date: string): number {
        return this.toMoment(date).daysInMonth();
    }

    public clone(date: string): string {
        return this._createMoment(date).clone().locale(this.locale).format('YYYY-MM-DD');
    }

    public toMoment(date: string): Moment {
        return this._createMoment(date).clone().locale(this.locale);
    }

    public createDate(year: number, month: number, date: number): string {
        // Moment.js will create an invalid date if any of the components are out of bounds, but we
        // explicitly check each case so we can throw more descriptive errors.
        /* if (typeof ngDevMode === 'undefined' || ngDevMode) {
            if (month < 0 || month > 11) {
                throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
            }

            if (date < 1) {
                throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
            }
        }*/

        const result = this._createMoment({ year, month, date }).locale(this.locale);

        // If the result isn't valid, the date must have been out of bounds for this month.
        /* if (!result.isValid() && (typeof ngDevMode === 'undefined' || ngDevMode)) {
            throw Error(`Invalid date "${date}" for month with index "${month}".`);
        }*/

        return result.format('YYYY-MM-DD');
    }

    public today(): string {
        return this._createMoment({}).locale(this.locale).format('YYYY-MM-DD');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public parse(value: any, parseFormat: string | string[]): string | null {
        if (value && typeof value == 'string') {
            return this._createMoment(value, parseFormat, this.locale).format('YYYY-MM-DD');
        }
        return value ? this._createMoment(value).locale(this.locale).format('YYYY-MM-DD') : null;
    }

    public format(date: string, displayFormat: string): string {

        /*  if (!this.isValid(date) && (typeof ngDevMode === 'undefined' || ngDevMode)) {
              throw Error('MomentDateAdapter: Cannot format invalid date.');
          }*/
        return this.toMoment(date).format(displayFormat);
    }

    public addCalendarYears(date: string, years: number): string {
        return this.toMoment(date).add({ years }).format('YYYY-MM-DD');
    }

    public addCalendarMonths(date: string, months: number): string {
        return this.toMoment(date).add({ months }).format('YYYY-MM-DD');
    }

    public addCalendarDays(date: string, days: number): string {
        return this.toMoment(date).add({ days }).format('YYYY-MM-DD');
    }

    public toIso8601(date: string): string {
        return this.toMoment(date).format();
    }

    /**
     * Returns the given value if given a valid Moment or null. Deserializes valid ISO 8601 strings
     * (https://www.ietf.org/rfc/rfc3339.txt) and valid Date objects into valid Moments and empty
     * string into null. Returns an invalid date for all other values.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public deserialize(value: any): string | null {
        let date;
        if (value instanceof Date) {
            date = this._createMoment(value).locale(this.locale);
        } else if (this.isDateInstance(value)) {
            // Note: assumes that cloning also sets the correct locale.
            return this.toMoment(value).format('YYYY-MM-DD');
        }
        if (typeof value === 'string') {
            if (!value) {
                return null;
            }
            date = this._createMoment(value, moment.ISO_8601).locale(this.locale);
        }
        if (date && this.isValid(date)) {
            return this._createMoment(date).locale(this.locale).format('YYYY-MM-DD');
        }
        return super.deserialize(value);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public isDateInstance(obj: any): boolean {
        return typeof obj === 'string' && !!obj;
    }

    public isValid(date: string): boolean {
        return this.toMoment(date).isValid();
    }

    public invalid(): string {
        return moment.invalid().format('YYYY-MM-DD');
    }

    /** Creates a Moment instance while respecting the current UTC settings. */
    private _createMoment(
        date: MomentInput,
        format?: MomentFormatSpecification,
        locale?: string,
    ): Moment {
        const { strict, useUtc }: IMatErpDateAdapterOptions = this._options || {};

        return useUtc
            ? moment.utc(date, format, locale, strict)
            : moment(date, format, locale, strict);
    }
}
